import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  Fade,
  Slide,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Autocomplete,
  Grid,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import StatusIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import profileImage from "assets/MRwall.png";
import ScoreDashboard from "components/ScoreDashboard";
import {
  useGetMrStatusRecordQuery,
  useUploadPrescriptionMutation,
  useUploadDrDataMutation,
  useGetDocRepQuery,
  useUploadChemistPobMutation,
} from "state/api";

// StatusRecords Component
const StatusRecords = ({
  openStatusList,
  records,
  filterStatus,
  setFilterStatus,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleStatusChange = (event, newStatus) => {
    if (newStatus !== null) {
      setFilterStatus(newStatus);
    }
  };

  // Filter records based on the selected status
  const filteredRecords = Array.isArray(records)
    ? records.filter((status) => status.STATUS === filterStatus)
    : [];

  return (
    <Slide direction="left" in={openStatusList} mountOnEnter unmountOnExit>
      <Card
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: isMobile ? "100%" : 380,
          height: "100vh",
          zIndex: 1000,
          overflowY: "auto",
          backgroundColor: "#1e293b",
          borderRight: isMobile ? "none" : "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.3)",
          backdropFilter: "blur(10px)",
        }}
      >
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          {/* Heading */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              pb: 2,
              borderBottom: "2px solid rgba(59, 130, 246, 0.3)",
            }}
          >
            <Typography variant="h5" fontWeight={700} sx={{ color: "white" }}>
              Status Records
            </Typography>
          </Box>

          {/* Toggle Buttons */}
          <ToggleButtonGroup
            value={filterStatus}
            exclusive
            onChange={handleStatusChange}
            fullWidth
            sx={{
              marginBottom: 3,
              "& .MuiToggleButton-root": {
                color: "rgba(255, 255, 255, 0.7)",
                borderColor: "rgba(255, 255, 255, 0.2)",
                "&.Mui-selected": {
                  color: "white",
                  backgroundColor: "transparent",
                },
              },
            }}
          >
            <ToggleButton
              value="APPROVED"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(16, 185, 129, 0.2)",
                  borderColor: "#10b981",
                },
              }}
            >
              Approved
            </ToggleButton>
            <ToggleButton
              value="REJECTED"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(239, 68, 68, 0.2)",
                  borderColor: "#ef4444",
                },
              }}
            >
              Rejected
            </ToggleButton>
            <ToggleButton
              value="PENDING"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(168, 85, 247, 0.2)",
                  borderColor: "#a855f7",
                },
              }}
            >
              Pending
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Status Grid */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {filteredRecords.length > 0 ? (
              filteredRecords.map((status, index) => (
                <Card
                  key={index}
                  sx={{
                    backgroundColor: "#0f172a",
                    borderLeft: "4px solid #3b82f6",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                    transition: "all 0.3s",
                    "&:hover": {
                      transform: "translateX(4px)",
                      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                    },
                  }}
                >
                  <CardContent sx={{ py: 2, px: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}
                    >
                      <strong>Dr Name:</strong> {status.DoctName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}
                    >
                      <strong>Date:</strong>{" "}
                      {new Date(status.SUBDATE).toLocaleDateString("en-GB")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          status.STATUS === "APPROVED"
                            ? "#10b981"
                            : status.STATUS === "REJECTED"
                              ? "#ef4444"
                              : "#a855f7",
                        fontWeight: 600,
                      }}
                    >
                      <strong>Status:</strong> {status.STATUS}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  textAlign: "center",
                  py: 4,
                }}
              >
                No records available for{" "}
                {filterStatus?.toLowerCase() || "selected"} status.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Slide>
  );
};

// Main Component
export default function Mr() {
  const [doctCatId, setDoctCatId] = useState(null); // New state for DoctCatId
  const [hqMstId, setHqMstId] = useState(null); // New state for HqMstId
  const [isPrescription, setIsPrescription] = useState(true);
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [openShutter, setOpenShutter] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [mclCode, setMclCode] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openStatusList, setOpenStatusList] = useState(false);
  const [filterStatus, setFilterStatus] = useState("APPROVED"); // Added filterStatus state
  const [selectedOption, setSelectedOption] = useState("EUKROMA KJ");
  const [selectedTab, setSelectedTab] = useState("PRESCRIPTION");

  const MgCd = useSelector((state) => state.global.MgCd);
  const EmpNo = useSelector((state) => state.global.EmpNo);

  const { data: apiResponse = { data: [] }, error } = useGetDocRepQuery(MgCd);
  const docReps = useMemo(() => apiResponse.data || [], [apiResponse.data]);
  const [uploadPrescription] = useUploadPrescriptionMutation();
  const [uploadDoctorData] = useUploadDrDataMutation();
  const { data: mrStatusResponse } = useGetMrStatusRecordQuery(EmpNo);
  const mrStatusRecord = mrStatusResponse?.data || [];
  const [fileError, setFileError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [strips, setStrips] = useState("");
  const [chemistName, setChemistName] = useState("");
  const [chemistAddress, setChemistAddress] = useState("");
  const [dlNo, setDlNo] = useState("");
  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [invoice, setInvoice] = useState({
    strips: "",
    chemistName: "",
    chemistAddress: "",
    dlNo: "",
    state: "",
    email: "",
    phone: "",
    pobType: "",
    pobProduct: "",
  });
  const [invoiceFileError, setInvoiceFileError] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);

  const [uploadInvoice] = useUploadChemistPobMutation();

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;
    // Special handling for phone number
    if (name === "phone") {
      if (/^\d{0,10}$/.test(value)) {
        setInvoice((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setInvoice((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleInvoiceClear = () => {
    setInvoice({
      strips: "",
      chemistName: "",
      chemistAddress: "",
      dlNo: "",
      state: "",
      email: "",
      phone: "",
      pobType: "",
      pobProduct: "",
    });
    setInvoiceFile(null);
    setInvoiceFileError("");
  };

  const handleInvoiceFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (3MB = 3 * 1024 * 1024 bytes)
      if (file.size > 3 * 1024 * 1024) {
        setInvoiceFileError("File size should be less than 3MB");
        setInvoiceFile(null); // Clear any previously selected file
        setSnackbarMessage("File size exceeds 3MB limit");
        setOpenSnackbar(true);
      } else {
        setInvoiceFileError(null);
        setInvoiceFile(file);
      }
    }
  };

  useEffect(() => {
    if (error) {
      console.error("Error fetching document representations:", error);
      setSnackbarMessage("Failed to fetch document representations.");
      setOpenSnackbar(true);
    }
  }, [error]);

  useEffect(() => {
    if (docReps.length > 0) {
      setHqMstId(docReps[0].HqMstId);
    }
  }, [docReps]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (3MB = 3 * 1024 * 1024 bytes)
      if (file.size > 3 * 1024 * 1024) {
        setFileError("File size should be less than 3MB");
        setPrescriptionFile(null); // Clear any previously selected file
        setSnackbarMessage("File size exceeds 3MB limit");
        setOpenSnackbar(true);
      } else {
        setFileError(null);
        setPrescriptionFile(file);
      }
    }
  };

  // const productOptions = {
  //   "EUKROMA KJ": ["EUKROMA KJ"],
  //   "PRU Lotion": ["PRU Lotion"],
  //   "KTC Scalpe Solution": ["KTC Scalpe Solution"],
  //   "JP Tone Active/JR": ["JP Tone Active/JR"],
  // };

  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      setSelectedOption(newValue);
    }
  };

  const handleCloseShutter = () => {
    setOpenShutter(false);
  };

  const handleToggleChange = (event, newAlignment) => {
    if (newAlignment !== null) {
      setIsPrescription(newAlignment === "prescription");
      resetFields(newAlignment);
    }
  };

  const resetFields = (newAlignment) => {
    if (newAlignment === "prescription") {
      setSelectedDoctor("");
      setSelectedSpecialty("");
      setMclCode("");
      setPrescriptionFile(null);
    } else {
      setSelectedDoctor("");
      setSelectedSpecialty("");
    }
  };

  const handlePrescriptionSubmit = async (event) => {
    event.preventDefault();
    if (
      !selectedDoctor ||
      !selectedSpecialty ||
      !mclCode ||
      !prescriptionFile ||
      !MgCd ||
      !EmpNo
    ) {
      setSnackbarMessage(
        "Please fill in all required fields and upload the prescription.",
      );
      setOpenSnackbar(true);
      return;
    }

    const prescriptionFormData = new FormData();
    prescriptionFormData.append("product", selectedOption);
    prescriptionFormData.append("doctorName", selectedDoctor.name);
    prescriptionFormData.append("specialty", selectedSpecialty);
    prescriptionFormData.append("mclCode", mclCode);
    prescriptionFormData.append("prescription", prescriptionFile);
    prescriptionFormData.append("MgCd", MgCd);
    prescriptionFormData.append("EmpNo", EmpNo);

    try {
      await uploadPrescription(prescriptionFormData).unwrap();
      setSnackbarMessage("Prescription uploaded successfully!");
      setOpenSnackbar(true);
      resetFields("prescription");
    } catch (error) {
      console.error("Error uploading prescription:", error);
      setSnackbarMessage("Failed to upload prescription.");
      setOpenSnackbar(true);
    }
  };

  const handleInvoiceSubmit = async (event) => {
    event.preventDefault();
    if (
      !selectedDoctor ||
      !selectedSpecialty ||
      !mclCode ||
      !invoice.strips ||
      !invoice.chemistName ||
      !invoice.state ||
      !invoice.email ||
      !invoice.phone ||
      !invoice.pobType ||
      !invoice.pobProduct ||
      !invoiceFile ||
      !MgCd ||
      !EmpNo
    ) {
      setSnackbarMessage(
        "Please fill in all required fields and upload the invoice.",
      );
      setOpenSnackbar(true);
      return;
    }

    const invoiceFormData = new FormData();
    invoiceFormData.append("doctorName", selectedDoctor.name);
    invoiceFormData.append("specialty", selectedSpecialty);
    invoiceFormData.append("mclCode", mclCode);
    invoiceFormData.append("pobType", invoice.pobType);
    invoiceFormData.append("pobProduct", invoice.pobProduct);
    invoiceFormData.append("strips", invoice.strips);
    invoiceFormData.append("chemistName", invoice.chemistName);
    invoiceFormData.append("chemistAddress", invoice.chemistAddress);
    invoiceFormData.append("dlNo", invoice.dlNo);
    invoiceFormData.append("state", invoice.state);
    invoiceFormData.append("email", invoice.email);
    invoiceFormData.append("mobile", invoice.phone);
    invoiceFormData.append("invoiceFile", invoiceFile);
    invoiceFormData.append("MgCd", MgCd);
    invoiceFormData.append("EmpNo", EmpNo);

    try {
      await uploadInvoice(invoiceFormData).unwrap();
      setSnackbarMessage("Invoice uploaded successfully!");
      setOpenSnackbar(true);
      handleInvoiceClear();
    } catch (error) {
      console.error("Error uploading invoice:", error);
      setSnackbarMessage("Failed to upload invoice.");
      setOpenSnackbar(true);
    }
  };

  const handleDoctorSubmit = async (event) => {
    event.preventDefault();
    if (!selectedDoctor || !selectedSpecialty) {
      setSnackbarMessage("Please fill in all required fields.");
      setOpenSnackbar(true);
      return;
    }

    const doctorData = {
      MgCd: MgCd,
      HqMstId: hqMstId, // Use the extracted HqMstId
      doctorName: selectedDoctor.name,
      DoctCatId: doctCatId, // Use DoctCatId instead of DocCategory
      UpdatedByEmpNo: EmpNo,
    };

    console.log("Doctor Data:", doctorData);

    try {
      await uploadDoctorData(doctorData).unwrap();
      setSnackbarMessage("Doctor created successfully!");
      setOpenSnackbar(true);
      resetFields("doctor");
    } catch (error) {
      console.error("Error creating doctor:", error);
      setSnackbarMessage("Failed to create doctor.");
      setOpenSnackbar(true);
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const toggleStatusList = () => {
    setOpenStatusList((prev) => !prev);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
        background:
          "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
        position: "relative",
        p: isMobile ? 2 : isTablet ? 3 : 4,
        overflow: "auto",
      }}
    >
      <IconButton
        onClick={toggleStatusList}
        sx={{
          position: "absolute",
          top: isMobile ? 12 : 24,
          right: isMobile ? 12 : 24,
          zIndex: 999,
          backgroundColor: "rgba(59, 130, 246, 0.2)",
          color: "#3b82f6",
          border: "1px solid rgba(59, 130, 246, 0.3)",
          transition: "all 0.3s",
          width: isMobile ? 40 : 48,
          height: isMobile ? 40 : 48,
          "&:hover": {
            backgroundColor: "rgba(59, 130, 246, 0.3)",
            transform: "scale(1.1)",
          },
        }}
      >
        <StatusIcon />
      </IconButton>

      {/* StatusRecords Component */}
      <StatusRecords
        openStatusList={openStatusList}
        records={mrStatusRecord}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
      />

      {/* Header Section - Modern Mobile Design */}
      <Box
        sx={{
          width: "100%",
          maxWidth: isMobile ? "100%" : 500,
          mb: isMobile ? 3 : 4,
          mt: isMobile ? 2 : 2,
        }}
      >
        {/* Section Title */}
        <Typography
          variant="h6"
          sx={{
            color: "white",
            fontWeight: 700,
            mb: 2,
            fontSize: isMobile ? "1.2rem" : "1.5rem",
            textAlign: isMobile ? "center" : "left",
            textShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          Upload Records
        </Typography>

        {/* Modern Tab Buttons */}
        <Box display="flex" gap={isMobile ? 1.5 : 2} mb={isMobile ? 2 : 3}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              borderRadius: "16px",
              px: isMobile ? 2 : 4,
              py: isMobile ? 1.8 : 2,
              fontSize: isMobile ? "0.85rem" : "0.9rem",
              fontWeight: 700,
              background:
                selectedTab === "PRESCRIPTION"
                  ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                  : "rgba(255, 255, 255, 0.08)",
              color: "white",
              border:
                "1px solid" +
                (selectedTab === "PRESCRIPTION"
                  ? " rgba(59, 130, 246, 0.6)"
                  : " rgba(255, 255, 255, 0.1)"),
              boxShadow:
                selectedTab === "PRESCRIPTION"
                  ? "0 8px 24px rgba(59, 130, 246, 0.35)"
                  : "none",
              transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:active": {
                transform: "scale(0.98)",
              },
              "&:hover": {
                transform:
                  selectedTab === "PRESCRIPTION"
                    ? "translateY(-3px)"
                    : "translateY(-2px)",
                boxShadow:
                  selectedTab === "PRESCRIPTION"
                    ? "0 12px 32px rgba(59, 130, 246, 0.4)"
                    : "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
            onClick={() => setSelectedTab("PRESCRIPTION")}
          >
            💊 Prescription
          </Button>
          <Button
            variant="contained"
            fullWidth
            sx={{
              borderRadius: "16px",
              px: isMobile ? 2 : 4,
              py: isMobile ? 1.8 : 2,
              fontSize: isMobile ? "0.85rem" : "0.9rem",
              fontWeight: 700,
              background:
                selectedTab === "INVOICE"
                  ? "linear-gradient(135deg, #a855f7 0%, #9333ea 100%)"
                  : "rgba(255, 255, 255, 0.08)",
              color: "white",
              border:
                "1px solid" +
                (selectedTab === "INVOICE"
                  ? " rgba(168, 85, 247, 0.6)"
                  : " rgba(255, 255, 255, 0.1)"),
              boxShadow:
                selectedTab === "INVOICE"
                  ? "0 8px 24px rgba(168, 85, 247, 0.35)"
                  : "none",
              transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:active": {
                transform: "scale(0.98)",
              },
              "&:hover": {
                transform:
                  selectedTab === "INVOICE"
                    ? "translateY(-3px)"
                    : "translateY(-2px)",
                boxShadow:
                  selectedTab === "INVOICE"
                    ? "0 12px 32px rgba(168, 85, 247, 0.4)"
                    : "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
            onClick={() => setSelectedTab("INVOICE")}
          >
            📋 POB
          </Button>
        </Box>
      </Box>
      {selectedTab === "PRESCRIPTION" && (
        <Fade in timeout={400}>
          <Card
            sx={{
              width: "100%",
              maxWidth: isMobile ? "100%" : 500,
              backgroundColor: "rgba(30, 41, 59, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(59, 130, 246, 0.25)",
              borderRadius: isMobile ? 3 : 3,
              boxShadow:
                "0 12px 48px rgba(59, 130, 246, 0.15), 0 4px 12px rgba(0, 0, 0, 0.4)",
              overflow: "hidden",
            }}
          >
            {/* Modern Gradient Header */}
            <Box
              sx={{
                background:
                  "linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1e40af 100%)",
                p: isMobile ? 2.5 : 3,
                borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
              }}
            >
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  color: "white",
                  fontWeight: 800,
                  textShadow: "0 2px 6px rgba(0,0,0,0.3)",
                  fontSize: isMobile ? "1.15rem" : "1.5rem",
                  letterSpacing: "0.5px",
                }}
              >
                💊 Prescription Upload
              </Typography>
            </Box>

            <CardContent
              sx={{ p: isMobile ? 2.5 : 3.5, backgroundColor: "transparent" }}
            >
              <form
                onSubmit={
                  isPrescription ? handlePrescriptionSubmit : handleDoctorSubmit
                }
                encType="multipart/form-data"
              >
                <>
                  {/* Product Dropdown */}
                  <TextField
                    select
                    fullWidth
                    label="Product"
                    variant="outlined"
                    margin="normal"
                    required
                    value={selectedOption}
                    onChange={(e) => setSelectedOption(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "white",
                        "& fieldset": {
                          borderColor: "rgba(59, 130, 246, 0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(59, 130, 246, 0.5)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3b82f6",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                    }}
                  >
                    <MenuItem value="EUKROMA KJ">EUKROMA KJ</MenuItem>
                    <MenuItem value="PRU Lotion">PRU Lotion</MenuItem>
                    <MenuItem value="KTC Scalpe Solution">
                      KTC Scalpe Solution
                    </MenuItem>
                    <MenuItem value="JP Tone Active/JR">
                      JP Tone Active/JR
                    </MenuItem>
                    <MenuItem value="VENTOBIL - M">VENTOBIL - M</MenuItem>
                    <MenuItem value="SUNKROMA ADVANCED">
                      SUNKROMA ADVANCED
                    </MenuItem>
                  </TextField>
                  <input type="hidden" name="product" value={selectedOption} />

                  <Autocomplete
                    options={[
                      ...new Set(docReps.map((doc) => doc.DocCategory)),
                    ]}
                    value={selectedSpecialty}
                    onChange={(event, newValue) => {
                      setSelectedSpecialty(newValue || "");
                      setSelectedDoctor(null);
                      setMclCode("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Specialty"
                        variant="outlined"
                        margin="normal"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            color: "white",
                            "& fieldset": {
                              borderColor: "rgba(59, 130, 246, 0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(59, 130, 246, 0.5)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3b82f6",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(255, 255, 255, 0.7)",
                          },
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    options={docReps
                      .filter((doc) => doc.DocCategory === selectedSpecialty)
                      .map((doc) => ({ name: doc.DoctName, code: doc.DoctCd }))}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedDoctor}
                    onChange={(event, newValue) => {
                      setSelectedDoctor(newValue);
                      setMclCode(newValue ? newValue.code : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Dr. Name"
                        variant="outlined"
                        margin="normal"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            color: "white",
                            "& fieldset": {
                              borderColor: "rgba(59, 130, 246, 0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(59, 130, 246, 0.5)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3b82f6",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(255, 255, 255, 0.7)",
                          },
                        }}
                      />
                    )}
                  />

                  <TextField
                    fullWidth
                    label="MCL Code"
                    variant="outlined"
                    margin="normal"
                    required
                    value={mclCode}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "white",
                        "& fieldset": {
                          borderColor: "rgba(59, 130, 246, 0.3)",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                    }}
                  />
                  <input type="hidden" name="mclCode" value={mclCode} />

                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    sx={{
                      mt: 2,
                      py: 1.5,
                      background:
                        "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)",
                      color: "white",
                      fontWeight: 600,
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(59, 130, 246, 0.4)",
                      },
                    }}
                  >
                    Upload Prescription
                    <input
                      type="file"
                      hidden
                      required
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      name="prescriptionFile"
                    />
                  </Button>

                  {fileError && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {fileError}
                    </Typography>
                  )}

                  <Fade in={!!prescriptionFile && !fileError} timeout={500}>
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: 1,
                        color: "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      {prescriptionFile
                        ? `${prescriptionFile.name} (${(
                            prescriptionFile.size /
                            (1024 * 1024)
                          ).toFixed(2)} MB)`
                        : ""}
                    </Typography>
                  </Fade>

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      marginTop: 2,
                      py: isMobile ? 1.6 : 1.8,
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "white",
                      fontWeight: 700,
                      borderRadius: "12px",
                      fontSize: isMobile ? "0.9rem" : "1rem",
                      transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)",
                      "&:hover:not(:disabled)": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 12px 32px rgba(16, 185, 129, 0.4)",
                      },
                      "&:active:not(:disabled)": {
                        transform: "scale(0.98)",
                      },
                      "&:disabled": {
                        opacity: 0.5,
                        background: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                    type="submit"
                    disabled={!!fileError || !prescriptionFile}
                  >
                    ✓ Submit
                  </Button>
                </>
              </form>

              <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
              />
            </CardContent>
          </Card>
        </Fade>
      )}
      {selectedTab === "INVOICE" && (
        <Fade in timeout={400}>
          <Card
            sx={{
              width: "100%",
              maxWidth: isMobile ? "100%" : 500,
              backgroundColor: "rgba(30, 41, 59, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(168, 85, 247, 0.25)",
              borderRadius: isMobile ? 3 : 3,
              boxShadow:
                "0 12px 48px rgba(168, 85, 247, 0.15), 0 4px 12px rgba(0, 0, 0, 0.4)",
              overflow: "hidden",
              mb: 10,
            }}
          >
            {/* Modern Gradient Header */}
            <Box
              sx={{
                background:
                  "linear-gradient(135deg, #a855f7 0%, #9333ea 50%, #7e22ce 100%)",
                p: isMobile ? 2.5 : 3,
                borderBottom: "1px solid rgba(168, 85, 247, 0.2)",
              }}
            >
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  color: "white",
                  fontWeight: 800,
                  textShadow: "0 2px 6px rgba(0,0,0,0.3)",
                  fontSize: isMobile ? "1.15rem" : "1.5rem",
                  letterSpacing: "0.5px",
                }}
              >
                📋 Invoice Upload
              </Typography>
            </Box>

            <CardContent
              sx={{ p: isMobile ? 2.5 : 3.5, backgroundColor: "transparent" }}
            >
              <form
                onSubmit={handleInvoiceSubmit}
                encType="multipart/form-data"
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  POB Type
                </Typography>
                <Box display="flex" gap={2} sx={{ mb: 3 }}>
                  <Card
                    onClick={() => {
                      setSelectedOption("Power Brand POB value");
                      setInvoice((prev) => ({
                        ...prev,
                        pobType: "Power Brand POB value",
                        pobProduct: "",
                      }));
                    }}
                    sx={{
                      flex: 1,
                      p: 2,
                      cursor: "pointer",
                      backgroundColor:
                        selectedOption === "Power Brand POB value"
                          ? "rgba(59, 130, 246, 0.2)"
                          : "#0f172a",
                      border:
                        "1px solid" +
                        (selectedOption === "Power Brand POB value"
                          ? " rgba(59, 130, 246, 0.5)"
                          : " rgba(255, 255, 255, 0.1)"),
                      transition: "all 0.3s",
                      "&:hover": {
                        backgroundColor: "rgba(59, 130, 246, 0.3)",
                        borderColor: "rgba(59, 130, 246, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color:
                            selectedOption === "Power Brand POB value"
                              ? "#3b82f6"
                              : "rgba(255, 255, 255, 0.7)",
                        }}
                      >
                        Power Brand POB
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#fbbf24",
                          fontSize: "0.50rem",
                          fontWeight: 500,
                        }}
                      >
                        (Eukroma, PRU, KTC)
                      </Typography>
                    </Box>
                  </Card>
                  <Card
                    onClick={() => {
                      setSelectedOption("JP Tone POB value");
                      setInvoice((prev) => ({
                        ...prev,
                        pobType: "JP Tone POB value",
                        pobProduct: "",
                      }));
                    }}
                    sx={{
                      flex: 1,
                      p: 2,
                      cursor: "pointer",
                      backgroundColor:
                        selectedOption === "JP Tone POB value"
                          ? "rgba(168, 85, 247, 0.2)"
                          : "#0f172a",
                      border:
                        "1px solid" +
                        (selectedOption === "JP Tone POB value"
                          ? " rgba(168, 85, 247, 0.5)"
                          : " rgba(255, 255, 255, 0.1)"),
                      transition: "all 0.3s",
                      "&:hover": {
                        backgroundColor: "rgba(168, 85, 247, 0.3)",
                        borderColor: "rgba(168, 85, 247, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color:
                          selectedOption === "JP Tone POB value"
                            ? "#a855f7"
                            : "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      Growth Brand
                    </Typography>
                  </Card>
                </Box>

                {/* Product Dropdown - Conditional based on POB Type */}
                {selectedOption === "Power Brand POB value" && (
                  <TextField
                    select
                    fullWidth
                    label="Product"
                    variant="outlined"
                    margin="normal"
                    required
                    value={invoice.pobProduct}
                    onChange={handleInvoiceChange}
                    name="pobProduct"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "white",
                        "& fieldset": {
                          borderColor: "rgba(59, 130, 246, 0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(59, 130, 246, 0.5)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3b82f6",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                    }}
                  >
                    <MenuItem value="KTC">KTC</MenuItem>
                    <MenuItem value="EUKROMA">EUKROMA</MenuItem>
                    <MenuItem value="PRU">PRU</MenuItem>
                  </TextField>
                )}

                {selectedOption === "JP Tone POB value" && (
                  <TextField
                    select
                    fullWidth
                    label="Product"
                    variant="outlined"
                    margin="normal"
                    required
                    value={invoice.pobProduct}
                    onChange={handleInvoiceChange}
                    name="pobProduct"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "white",
                        "& fieldset": {
                          borderColor: "rgba(168, 85, 247, 0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(168, 85, 247, 0.5)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#a855f7",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                    }}
                  >
                    <MenuItem value="JP TONE ACTIVE">JP TONE ACTIVE</MenuItem>
                    <MenuItem value="JP TONE JUNIOR">JP TONE JUNIOR</MenuItem>
                    <MenuItem value="VENTOBIL M">VENTOBIL M</MenuItem>
                    <MenuItem value="SUNKROMA ADVANCED">
                      SUNKROMA ADVANCED
                    </MenuItem>
                  </TextField>
                )}

                <Autocomplete
                  options={[...new Set(docReps.map((doc) => doc.DocCategory))]}
                  value={selectedSpecialty}
                  onChange={(event, newValue) => {
                    setSelectedSpecialty(newValue || "");
                    setSelectedDoctor(null);
                    setMclCode("");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Specialty"
                      variant="outlined"
                      margin="normal"
                      required
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": {
                            borderColor: "rgba(59, 130, 246, 0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(59, 130, 246, 0.5)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3b82f6",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255, 255, 255, 0.7)",
                        },
                      }}
                    />
                  )}
                />

                <Autocomplete
                  options={docReps
                    .filter((doc) => doc.DocCategory === selectedSpecialty)
                    .map((doc) => ({ name: doc.DoctName, code: doc.DoctCd }))}
                  getOptionLabel={(option) => option.name || ""}
                  value={selectedDoctor}
                  onChange={(event, newValue) => {
                    setSelectedDoctor(newValue);
                    setMclCode(newValue ? newValue.code : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dr. Name"
                      variant="outlined"
                      margin="normal"
                      required
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": {
                            borderColor: "rgba(59, 130, 246, 0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(59, 130, 246, 0.5)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3b82f6",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255, 255, 255, 0.7)",
                        },
                      }}
                    />
                  )}
                />

                <TextField
                  fullWidth
                  label="MCL Code"
                  variant="outlined"
                  margin="normal"
                  required
                  value={mclCode}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />
                <input type="hidden" name="mclCode" value={mclCode} />

                <TextField
                  fullWidth
                  label="No of Units"
                  variant="outlined"
                  margin="normal"
                  name="strips"
                  value={invoice.strips}
                  onChange={handleInvoiceChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Chemist Name"
                  variant="outlined"
                  margin="normal"
                  name="chemistName"
                  value={invoice.chemistName}
                  onChange={handleInvoiceChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Chemist Address"
                  variant="outlined"
                  margin="normal"
                  name="chemistAddress"
                  value={invoice.chemistAddress}
                  onChange={handleInvoiceChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="DL Number"
                  variant="outlined"
                  margin="normal"
                  name="dlNo"
                  value={invoice.dlNo}
                  onChange={handleInvoiceChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />

                <Autocomplete
                  options={[
                    "Andhra Pradesh",
                    "Arunachal Pradesh",
                    "Assam",
                    "Bihar",
                    "Chhattisgarh",
                    "Goa",
                    "Gujarat",
                    "Haryana",
                    "Himachal Pradesh",
                    "Jharkhand",
                    "Karnataka",
                    "Kerala",
                    "Madhya Pradesh",
                    "Maharashtra",
                    "Manipur",
                    "Meghalaya",
                    "Mizoram",
                    "Nagaland",
                    "Odisha",
                    "Punjab",
                    "Rajasthan",
                    "Sikkim",
                    "Tamil Nadu",
                    "Telangana",
                    "Tripura",
                    "Uttar Pradesh",
                    "Uttarakhand",
                    "West Bengal",
                  ]}
                  value={invoice.state}
                  onChange={(event, newValue) => {
                    setInvoice((prev) => ({ ...prev, state: newValue || "" }));
                  }}
                  freeSolo={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      variant="outlined"
                      margin="normal"
                      name="state"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": {
                            borderColor: "rgba(59, 130, 246, 0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(59, 130, 246, 0.5)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3b82f6",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255, 255, 255, 0.7)",
                        },
                      }}
                    />
                  )}
                />

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  margin="normal"
                  name="email"
                  value={invoice.email}
                  onChange={handleInvoiceChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />

                <TextField
                  label="Phone Number"
                  variant="outlined"
                  fullWidth
                  name="phone"
                  value={invoice.phone}
                  onChange={handleInvoiceChange}
                  error={
                    invoice.phone.length > 0 && invoice.phone.length !== 10
                  }
                  helperText={
                    invoice.phone.length > 0 && invoice.phone.length !== 10
                      ? "Phone number must be exactly 10 digits"
                      : ""
                  }
                  required
                  margin="normal"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(59, 130, 246, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#ef4444",
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />

                <Button
                  variant="contained"
                  component="label"
                  fullWidth
                  sx={{
                    marginTop: 2,
                    py: 1.5,
                    background:
                      "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)",
                    color: "white",
                    fontWeight: 600,
                    transition: "all 0.3s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(168, 85, 247, 0.4)",
                    },
                  }}
                >
                  Upload Invoice
                  <input
                    type="file"
                    hidden
                    required
                    onChange={handleInvoiceFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    name="invoiceFile"
                  />
                </Button>

                {invoiceFileError && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {invoiceFileError}
                  </Typography>
                )}

                <Fade in={!!invoiceFile} timeout={500}>
                  <Typography
                    variant="body2"
                    sx={{
                      marginTop: 1,
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    {invoiceFile
                      ? `${invoiceFile.name} (${(
                          invoiceFile.size /
                          (1024 * 1024)
                        ).toFixed(2)} MB)`
                      : ""}
                  </Typography>
                </Fade>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  mt={3}
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleInvoiceClear}
                    sx={{
                      py: 1.5,
                      color: "rgba(255, 255, 255, 0.7)",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                      transition: "all 0.3s",
                      "&:hover": {
                        borderColor: "rgba(255, 255, 255, 0.4)",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    type="submit"
                    disabled={invoice.phone?.length !== 10}
                    sx={{
                      py: 1.5,
                      background:
                        "linear-gradient(45deg, #10b981 0%, #059669 100%)",
                      color: "white",
                      fontWeight: 600,
                      transition: "all 0.3s",
                      "&:hover:not(:disabled)": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(16, 185, 129, 0.4)",
                      },
                      "&:disabled": {
                        opacity: 0.5,
                        background: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </form>

              <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
              />
            </CardContent>
          </Card>
        </Fade>
      )}
    </Box>
  );
}
